import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useIntl} from 'react-intl';

import {ReactComponent as EmptyHeart} from 'assets/image/library/empty-heart.svg';
import {ReactComponent as FullHeart} from 'assets/image/library/full-heart.svg';

import {useLoadFile} from 'utils/hooks/useLoadFile';

import {ICmService} from '../../redux/cm-service/interfaces';
import {
  cmServiceAddToFavorites,
  cmServiceAsyncActions,
  cmServiceRemoveFromFavorites
} from '../../redux/pageAsyncActions';

import './styles.scss';

const MAX_VISIBLE_TAGS = 3;

type Props = {
  card: ICmService;
  onClick: () => void;
}

export const DmResourceCard = ({card, onClick}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {getFileUrl} = useLoadFile();
  const [favorite, setFavorite] = useState(card.favorite);
  const [imageUrl, setImageUrl] = useState<string | null>(card.srvImageUrl || null);

  useEffect(() => {
    setFavorite(card.favorite);
  }, [card.favorite]);

  useEffect(() => {
    if (!card.srvImageUrl && card.srvImageId) {
      getFileUrl(card.srvImageId, (url) => {
        if (url) {
          setImageUrl(url);
        }
      });
    } else {
      setImageUrl(card.srvImageUrl || null);
    }
  }, [card.srvImageId, card.srvImageUrl]);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !favorite;

    setFavorite(next);
    if (next) {
      dispatch(cmServiceAsyncActions[cmServiceAddToFavorites](card.srvId));
    } else {
      dispatch(cmServiceAsyncActions[cmServiceRemoveFromFavorites](card.srvId));
    }
  };

  const tags = card.typeTreatments
    ? card.typeTreatments.split(',').map(t => t.trim()).filter(Boolean)
    : [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTagCount = tags.length - MAX_VISIBLE_TAGS;

  return (
    <>
      {/*<div className="dm-resource-card-accent"></div>*/}
      <div className="dm-resource-card" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>
        <button
          type="button"
          className="dm-resource-card__favorite-btn"
          onClick={handleFavoriteClick}
          aria-label={favorite
            ? intl.formatMessage({id: 'gritx.cmresource.card.removeFromFavorites', defaultMessage: 'Remove from favorites'})
            : intl.formatMessage({id: 'gritx.cmresource.card.addToFavorites', defaultMessage: 'Add to favorites'})
          }
        >
          {favorite
            ? <FullHeart className="dm-resource-card__heart-icon" />
            : <EmptyHeart className="dm-resource-card__heart-icon" />
          }
        </button>

        <div className="dm-resource-card__header">
          <div className="dm-resource-card__badges">
            {card.subcategoryName && (
              <span className="dm-resource-card__badge">{card.subcategoryName}</span>
            )}
            {card.cmCategoryName && (
              <span className="dm-resource-card__badge dm-resource-card__badge--accent">{String(card.cmCategoryName)}</span>
            )}
          </div>
        </div>

        <div className="dm-resource-card__body">
          <div className="dm-resource-card__title">{card.srvName}</div>
          {card.orgName && (
            <div className="dm-resource-card__org">{card.orgName}</div>
          )}
        </div>

        {visibleTags.length > 0 && (
          <div className="dm-resource-card__tags">
            {visibleTags.map((tag, i) => (
              <span key={i} className="dm-resource-card__tag">{tag}</span>
            ))}
            {hiddenTagCount > 0 && (
              <span className="dm-resource-card__tag dm-resource-card__tag--overflow">+{hiddenTagCount}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
};
